import { FormControl, FormGroup } from '@angular/forms';
import { Guid, Nillable } from '@web/utils';
import { InPostPointSelect } from '@web/shared/ui';
import { PhoneNumberWithPrefix } from '@web/shared/module/dictionary';

export type PublicCheckoutFormControls = {
  regulations: FormControl<boolean>;
  newsletter: FormControl<boolean>;
  billingAddress: PublicCheckoutBillingFormGroup;
  comment: FormControl<Nillable<string>>;
  otherShippingAddress: FormControl<boolean>;
  paymentMethod: FormControl<Nillable<Guid>>;
  shippingAddress: PublicCheckoutShippingFormGroup;
  shippingMethodId: FormControl<Nillable<Guid>>;
  shippingPoint: FormControl<Nillable<InPostPointSelect>>;
};

export type PublicCheckoutBillingFormControls = {
  city: FormControl<string>;
  companyName: FormControl<Nillable<string>>;
  companyNip: FormControl<Nillable<string>>;
  countryId: FormControl<Nillable<Guid>>;
  email: FormControl<string>;
  firstName: FormControl<string>;
  lastName: FormControl<string>;
  phone: FormControl<Nillable<PhoneNumberWithPrefix>>;
  postCode: FormControl<string>;
  state: FormControl<Nillable<string>>;
  streetLine1: FormControl<string>;
  streetLine2: FormControl<Nillable<string>>;
};

export type PublicCheckoutShippingFormControls = {
  city: FormControl<string>;
  companyName: FormControl<Nillable<string>>;
  countryId: FormControl<Nillable<Guid>>;
  email: FormControl<string>;
  firstName: FormControl<string>;
  lastName: FormControl<string>;
  phone: FormControl<Nillable<PhoneNumberWithPrefix>>;
  postCode: FormControl<string>;
  state: FormControl<Nillable<string>>;
  streetLine1: FormControl<string>;
  streetLine2: FormControl<Nillable<string>>;
};

export class PublicCheckoutFormData {
  public static firstNameMaxLength = 100;
  public static lastNameMaxLength = 100;
  public static emailMaxLength = 255;
  public static streetLine1MaxLength = 255;
  public static streetLine2MaxLength = 255;
  public static postCodeMaxLength = 30;
  public static cityMaxLength = 100;
  public static stateMaxLength = 100;
  public static companyNameMaxLength = 255;
  public static companyNipMaxLength = 100;
  public static commentMaxLength = 500;

  public constructor(
    private readonly data: {
      regulations: boolean;
      newsletter: boolean;
      billingAddress: {
        streetLine1: string;
        streetLine2: Nillable<string>;
        city: string;
        companyName: Nillable<string>;
        countryId: Guid;
        email: string;
        firstName: string;
        lastName: string;
        companyNip: Nillable<string>;
        phone: Nillable<PhoneNumberWithPrefix>;
        postCode: string;
        state: Nillable<string>;
      };
      shippingAddress?: Nillable<{
        streetLine1: string;
        streetLine2: Nillable<string>;
        city: string;
        companyName: Nillable<string>;
        countryId: Guid;
        email: string;
        firstName: string;
        lastName: string;
        phone: Nillable<PhoneNumberWithPrefix>;
        postCode: string;
        state: Nillable<string>;
      }>;
      comment: Nillable<string>;
      otherShippingAddress: boolean;
      shippingMethodId: Guid;
      paymentMethod: Guid;
      shippingPoint: Nillable<InPostPointSelect>;
    },
  ) {}

  public get billingAddress(): {
    streetLine1: string;
    streetLine2: Nillable<string>;
    city: string;
    companyName: Nillable<string>;
    countryId: Guid;
    email: string;
    firstName: string;
    lastName: string;
    companyNip: Nillable<string>;
    phone: Nillable<PhoneNumberWithPrefix>;
    postCode: string;
    state: Nillable<string>;
  } {
    return this.data.billingAddress;
  }

  public get shippingAddress(): Nillable<{
    streetLine1: string;
    streetLine2: Nillable<string>;
    city: string;
    companyName: Nillable<string>;
    countryId: Guid;
    email: string;
    firstName: string;
    lastName: string;
    phone: Nillable<PhoneNumberWithPrefix>;
    postCode: string;
    state: Nillable<string>;
  }> {
    return this.data.shippingAddress;
  }

  public get comment(): Nillable<string> {
    return this.data.comment;
  }

  public get otherShippingAddress(): boolean {
    return this.data.otherShippingAddress;
  }

  public get shippingMethodId(): Guid {
    return this.data.shippingMethodId;
  }

  public get paymentMethod(): Guid {
    return this.data.paymentMethod;
  }

  public get shippingPoint(): Nillable<InPostPointSelect> {
    return this.data.shippingPoint;
  }

  public get newsletter(): boolean {
    return this.data.newsletter;
  }
}

/* prettier-ignore */
export type PublicCheckoutFormGroup = FormGroup<PublicCheckoutFormControls>;
/* prettier-ignore */
export type PublicCheckoutBillingFormGroup = FormGroup<PublicCheckoutBillingFormControls>;
/* prettier-ignore */
export type PublicCheckoutShippingFormGroup = FormGroup<PublicCheckoutShippingFormControls>;
